// NOVO FICHEIRO: client/src/components/EscalaoRequest/EscalaoRequestCard.jsx

import React, { useState, useEffect, useRef } from 'react';
import { FaFileUpload, FaSpinner, FaCheckCircle, FaTimesCircle, FaClock, FaFilePdf, FaTrash, FaInfoCircle } from 'react-icons/fa';
import './EscalaoRequestCard.css';
import API_BASE_URL from '../../config/api';

const STATUS_MAP = {
    0: { label: 'Pendente', icon: <FaClock />, cls: 'pending' },
    1: { label: 'Aceite', icon: <FaCheckCircle />, cls: 'accepted' },
    2: { label: 'Recusado', icon: <FaTimesCircle />, cls: 'rejected' },
};

const DOC_CONFIGS = [
    {
        key: 'irs',
        label: 'Nota de Liquidação de IRS',
        description: 'Nota de liquidação de IRS do agregado familiar',
        required: true
    },
    {
        key: 'household',
        label: 'Composição do Agregado Familiar',
        description: 'Comprovativo da composição do agregado familiar (ex: Portal das Finanças / Junta)',
        required: true
    },
    {
        key: 'appForm',
        label: 'Questionário de Candidatura',
        description: 'Questionário de candidatura devidamente preenchido e assinado',
        required: true
    },
    {
        key: 'unemployment',
        label: 'Comprovativo de Desemprego',
        description: 'Comprovativo de situação de desemprego (quando aplicável)',
        required: false
    }
];

const EscalaoRequestCard = ({ userId, athleteProfileEscalao }) => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    
    // Files state: { irs: File, household: File, appForm: File, unemployment: File }
    const [files, setFiles] = useState({
        irs: null,
        household: null,
        appForm: null,
        unemployment: null
    });
    
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const inputRefs = {
        irs: useRef(),
        household: useRef(),
        appForm: useRef(),
        unemployment: useRef()
    };

    const fetchMyRequests = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/api/escalao-requests/my?userId=${userId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) setRequests(await res.json());
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userId) fetchMyRequests();
    }, [userId]);

    const hasPending = requests.some(r => r.status === 0);
    const isEscalao1 = athleteProfileEscalao === 'Escalão 1';

    const handleFileChange = (key, e) => {
        const f = e.target.files[0];
        if (!f) return;
        if (f.type !== 'application/pdf') {
            setError(`O ficheiro de "${DOC_CONFIGS.find(d => d.key === key).label}" tem de ser formato PDF.`);
            return;
        }
        if (f.size > 5 * 1024 * 1024) {
            setError(`O ficheiro não pode exceder 5MB.`);
            return;
        }
        setError('');
        setFiles(prev => ({ ...prev, [key]: f }));
    };

    const handleRemoveFile = (key, e) => {
        e.stopPropagation();
        setFiles(prev => ({ ...prev, [key]: null }));
        if (inputRefs[key]?.current) {
            inputRefs[key].current.value = '';
        }
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '';
        const kb = bytes / 1024;
        if (kb < 1024) return `${Math.round(kb)} KB`;
        return `${(kb / 1024).toFixed(1)} MB`;
    };

    const handleSubmit = async () => {
        if (!files.irs || !files.household || !files.appForm) {
            setError('Por favor, anexe os 3 documentos obrigatórios antes de submeter.');
            return;
        }

        setSubmitting(true);
        setError('');
        setSuccess('');

        try {
            const token = localStorage.getItem('token');
            const formData = new FormData();
            formData.append('irsDocument', files.irs);
            formData.append('householdDocument', files.household);
            formData.append('applicationForm', files.appForm);
            if (files.unemployment) {
                formData.append('unemploymentDocument', files.unemployment);
            }
            if (userId) formData.append('userId', userId);

            const res = await fetch(`${API_BASE_URL}/api/escalao-requests`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message);
            
            setSuccess(data.message);
            setFiles({ irs: null, household: null, appForm: null, unemployment: null });
            Object.values(inputRefs).forEach(ref => {
                if (ref.current) ref.current.value = '';
            });
            fetchMyRequests();
        } catch (e) {
            setError(e.message);
        } finally {
            setSubmitting(false);
        }
    };

    const formatDate = (d) => d ? new Date(d).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

    const canSubmit = files.irs && files.household && files.appForm;

    return (
        <div className="escalao-card">
            <div className="escalao-card-header">
                <h2><FaFilePdf /> Candidatura / Pedido de Escalão</h2>
            </div>

            <div className="escalao-card-body">

                {/* Current escalão badge */}
                <div className="escalao-current">
                    <span className="escalao-label">Escalão atual:</span>
                    <span className={`escalao-badge ${isEscalao1 ? 'escalao-badge--1' : 'escalao-badge--2'}`}>
                        {athleteProfileEscalao || 'Sem escalão (Normal)'}
                    </span>
                </div>

                {isEscalao1 ? (
                    <div className="escalao-info escalao-info--ok">
                        <FaCheckCircle /> Já beneficias do Escalão 1.
                    </div>
                ) : (
                    <>
                        <div className="escalao-intro-notice">
                            <FaInfoCircle className="escalao-notice-icon" />
                            <p>
                                A candidatura a escalão de apoio social requer o envio dos seguintes documentos em formato PDF (máx. 5MB cada).
                            </p>
                        </div>

                        {/* Upload area */}
                        {!hasPending && (
                            <div className="escalao-upload-area">
                                <div className="escalao-doc-grid">
                                    {DOC_CONFIGS.map(doc => {
                                        const file = files[doc.key];
                                        return (
                                            <div
                                                key={doc.key}
                                                className={`escalao-doc-slot ${file ? 'has-file' : ''} ${doc.required ? 'is-required' : ''}`}
                                                onClick={() => inputRefs[doc.key].current?.click()}
                                            >
                                                <div className="escalao-doc-slot-header">
                                                    <span className="escalao-doc-title">
                                                        {doc.label}
                                                    </span>
                                                    <span className={`escalao-doc-badge ${doc.required ? 'badge-req' : 'badge-opt'}`}>
                                                        {doc.required ? 'Obrigatório' : 'Opcional'}
                                                    </span>
                                                </div>

                                                <p className="escalao-doc-desc">{doc.description}</p>

                                                {file ? (
                                                    <div className="escalao-slot-file-info">
                                                        <div className="escalao-slot-filename">
                                                            <FaFilePdf className="escalao-pdf-icon" />
                                                            <span title={file.name}>{file.name}</span>
                                                            <small className="escalao-slot-size">({formatFileSize(file.size)})</small>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className="escalao-slot-remove-btn"
                                                            onClick={(e) => handleRemoveFile(doc.key, e)}
                                                            title="Remover ficheiro"
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="escalao-slot-placeholder">
                                                        <FaFileUpload />
                                                        <span>Clique para anexar PDF</span>
                                                    </div>
                                                )}

                                                <input
                                                    ref={inputRefs[doc.key]}
                                                    type="file"
                                                    accept=".pdf"
                                                    onChange={(e) => handleFileChange(doc.key, e)}
                                                    style={{ display: 'none' }}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>

                                {error && <p className="escalao-error">{error}</p>}
                                {success && <p className="escalao-success">{success}</p>}

                                <button
                                    className="escalao-submit-btn"
                                    onClick={handleSubmit}
                                    disabled={submitting || !canSubmit}
                                >
                                    {submitting ? <FaSpinner className="icon-spin" /> : <FaFileUpload />}
                                    Submeter Candidatura de Escalão
                                </button>
                            </div>
                        )}

                        {hasPending && (
                            <div className="escalao-info escalao-info--pending">
                                <FaClock /> Pedido em análise. Aguarda a validação dos documentos pelo administrador.
                            </div>
                        )}
                    </>
                )}

                {/* History */}
                {requests.length > 0 && (
                    <div className="escalao-history">
                        <div className="escalao-history-title">Histórico de pedidos</div>
                        {requests.map(r => {
                            const s = STATUS_MAP[r.status] || STATUS_MAP[0];
                            return (
                                <div key={r.id} className={`escalao-history-item escalao-history-item--${s.cls}`}>
                                    <div className="escalao-history-left">
                                        {s.icon}
                                        <div>
                                            <span className="escalao-history-status">{s.label}</span>
                                            <span className="escalao-history-date">{formatDate(r.createdAt)}</span>
                                        </div>
                                    </div>
                                    {r.adminNote && <span className="escalao-history-note">"{r.adminNote}"</span>}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default EscalaoRequestCard;